import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import { AuthPage } from './features/auth/AuthPage';
import { ScheduleView } from './features/schedule/ScheduleView';
import { MonthlyCalendar } from './features/calendar/MonthlyCalendar';
import { CustomTasksPage } from './features/tasks/CustomTasksPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { CustomizeTaskListPage } from './features/customize/CustomizeTaskListPage';
import { BottomNav } from './components/BottomNav/BottomNav';
import { ConfirmDialog } from './components/ConfirmDialog/ConfirmDialog';
import { Toast } from './components/Toast/Toast';
import { persistence } from './utils/persistence';
import { getStoredToken, getStoredUser, storeAuth, clearAuth, apiVerifyToken } from './utils/auth';
import { scheduleData, getWeekKey, getDateForDayIndex } from './features/schedule/scheduleData';
import type {
  PageId, DaySchedule, CalendarHistory, CalendarEntry,
  CustomTask, AppSettings, ConfirmConfig, User,
} from './types';
import { DEFAULT_SETTINGS } from './types';

// Storage keys
const KEYS = {
  weeklyTasks: 'cleaning-schedule-state',
  calendarHistory: 'cleaning-calendar-history',
  customTasks: 'cleaning-custom-tasks',
  customSchedule: 'cleaning-custom-schedule',
  settings: 'cleaning-app-settings',
};

function App() {
  // Auth state
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  // App state
  const [page, setPage] = useState<PageId>('schedule');
  const [tasks, setTasks] = useState<Record<string, boolean>>({});
  const [calendarHistory, setCalendarHistory] = useState<CalendarHistory>({});
  const [customTasks, setCustomTasks] = useState<CustomTask[]>([]);
  const [customSchedule, setCustomSchedule] = useState<DaySchedule[] | null>(null);
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [confirmConfig, setConfirmConfig] = useState<ConfirmConfig | null>(null);

  const weekKey = getWeekKey();
  const effectiveSchedule = customSchedule || scheduleData;
  const effectiveRef = useRef(effectiveSchedule);
  effectiveRef.current = effectiveSchedule;

  // Cloud sync debounce ref
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Verify stored token on mount ---
  useEffect(() => {
    (async () => {
      const token = getStoredToken();
      if (token) {
        try {
          const user = await apiVerifyToken(token);
          setAuthToken(token);
          setAuthUser(user);
          persistence.setAuthToken(token);
          storeAuth(token, user);
        } catch {
          // Token expired or invalid
          clearAuth();
        }
      }
      setAuthLoading(false);
    })();
  }, []);

  // --- Handle auth success ---
  const handleAuth = useCallback((token: string, user: User) => {
    setAuthToken(token);
    setAuthUser(user);
    persistence.setAuthToken(token);
  }, []);

  // --- Logout ---
  const handleLogout = useCallback(() => {
    clearAuth();
    persistence.setAuthToken(null);
    setAuthToken(null);
    setAuthUser(null);
    setPage('schedule');
  }, []);

  // --- Load state (runs after auth is established) ---
  useEffect(() => {
    if (authLoading || !authUser) return;

    (async () => {
      try {
        // Load weekly tasks (with API sync)
        const weeklyTasks = await persistence.load(weekKey);
        setTasks(weeklyTasks);

        // Try loading from cloud first
        const cloudData = await persistence.loadAllFromCloud();
        if (cloudData) {
          // Cloud data found — use it and update localStorage
          if (cloudData.calendarHistory) {
            setCalendarHistory(cloudData.calendarHistory as CalendarHistory);
            persistence.setLocal(KEYS.calendarHistory, JSON.stringify(cloudData.calendarHistory));
          }
          if (cloudData.customTasks) {
            setCustomTasks(cloudData.customTasks as CustomTask[]);
            persistence.setLocal(KEYS.customTasks, JSON.stringify(cloudData.customTasks));
          }
          if (cloudData.customSchedule !== undefined) {
            setCustomSchedule(cloudData.customSchedule as DaySchedule[] | null);
            if (cloudData.customSchedule) {
              persistence.setLocal(KEYS.customSchedule, JSON.stringify(cloudData.customSchedule));
            }
          }
          if (cloudData.settings) {
            const merged = { ...DEFAULT_SETTINGS, ...(cloudData.settings as Partial<AppSettings>) };
            setSettings(merged as AppSettings);
            persistence.setLocal(KEYS.settings, JSON.stringify(merged));
          }
        } else {
          // No cloud data — load from localStorage
          const historyRaw = persistence.getLocal(KEYS.calendarHistory);
          if (historyRaw) setCalendarHistory(JSON.parse(historyRaw));

          const customTasksRaw = persistence.getLocal(KEYS.customTasks);
          if (customTasksRaw) setCustomTasks(JSON.parse(customTasksRaw));

          const scheduleRaw = persistence.getLocal(KEYS.customSchedule);
          if (scheduleRaw) setCustomSchedule(JSON.parse(scheduleRaw));

          const settingsRaw = persistence.getLocal(KEYS.settings);
          if (settingsRaw) setSettings({ ...DEFAULT_SETTINGS, ...JSON.parse(settingsRaw) });
        }
      } catch (e) {
        console.error('Failed to load state:', e);
      }
      setLoaded(true);
    })();
  }, [weekKey, authLoading, authUser]);

  // --- Debounced cloud sync ---
  const syncToCloud = useCallback(() => {
    if (!authToken) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      const historyRaw = persistence.getLocal(KEYS.calendarHistory);
      const customTasksRaw = persistence.getLocal(KEYS.customTasks);
      const scheduleRaw = persistence.getLocal(KEYS.customSchedule);
      const settingsRaw = persistence.getLocal(KEYS.settings);

      persistence.syncAllToCloud({
        calendarHistory: historyRaw ? JSON.parse(historyRaw) : {},
        customTasks: customTasksRaw ? JSON.parse(customTasksRaw) : [],
        customSchedule: scheduleRaw ? JSON.parse(scheduleRaw) : null,
        settings: settingsRaw ? JSON.parse(settingsRaw) : DEFAULT_SETTINGS,
      });
    }, 2000);
  }, [authToken]);

  // --- Save helpers ---
  const saveWeeklyAndCalendar = useCallback((newTasks: Record<string, boolean>) => {
    persistence.save(weekKey, newTasks);

    setCalendarHistory((prev) => {
      const next = { ...prev };
      const schedule = effectiveRef.current;
      for (let i = 0; i < schedule.length; i++) {
        const dateStr = getDateForDayIndex(i);
        const day = schedule[i];
        const completedIds = day.tasks.filter((t) => newTasks[t.id]).map((t) => t.id);
        next[dateStr] = {
          date: dateStr,
          dayIndex: i,
          completedIds,
          totalTasks: day.tasks.length,
        };
      }
      persistence.setLocal(KEYS.calendarHistory, JSON.stringify(next));
      syncToCloud();
      return next;
    });
  }, [weekKey, syncToCloud]);

  // --- Task toggle ---
  const toggleTask = useCallback((taskId: string) => {
    setTasks((prev) => {
      const newTasks = { ...prev, [taskId]: !prev[taskId] };
      saveWeeklyAndCalendar(newTasks);
      return newTasks;
    });
  }, [saveWeeklyAndCalendar]);

  // --- Reset week ---
  const resetWeek = useCallback(() => {
    setConfirmConfig({
      title: 'Reset Week',
      message: 'Reset all tasks for this week? This cannot be undone.',
      confirmLabel: 'Reset',
      danger: true,
      onConfirm: () => {
        setTasks({});
        persistence.reset(weekKey);
      },
    });
  }, [weekKey]);

  // --- Custom Tasks ---
  const addCustomTask = useCallback((data: Omit<CustomTask, 'id' | 'completed' | 'createdAt'>) => {
    setCustomTasks((prev) => {
      const newTask: CustomTask = {
        ...data,
        id: `ct-${Date.now()}`,
        completed: false,
        createdAt: new Date().toISOString(),
      };
      const next = [newTask, ...prev];
      persistence.setLocal(KEYS.customTasks, JSON.stringify(next));
      syncToCloud();
      return next;
    });
  }, [syncToCloud]);

  const toggleCustomTask = useCallback((id: string) => {
    setCustomTasks((prev) => {
      const next = prev.map((t) => t.id === id ? { ...t, completed: !t.completed } : t);
      persistence.setLocal(KEYS.customTasks, JSON.stringify(next));
      syncToCloud();
      return next;
    });
  }, [syncToCloud]);

  const deleteCustomTask = useCallback((id: string) => {
    setCustomTasks((prev) => {
      const next = prev.filter((t) => t.id !== id);
      persistence.setLocal(KEYS.customTasks, JSON.stringify(next));
      syncToCloud();
      return next;
    });
  }, [syncToCloud]);

  // --- Settings ---
  const updateSettings = useCallback((newSettings: AppSettings) => {
    setSettings(newSettings);
    persistence.setLocal(KEYS.settings, JSON.stringify(newSettings));
    syncToCloud();
  }, [syncToCloud]);

  // --- Custom Schedule ---
  const saveCustomSchedule = useCallback((schedule: DaySchedule[]) => {
    setCustomSchedule(schedule);
    persistence.setLocal(KEYS.customSchedule, JSON.stringify(schedule));
    syncToCloud();
  }, [syncToCloud]);

  // --- Export ---
  const exportData = useCallback(() => {
    const data = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      weeklyTasks: { weekKey, tasks },
      calendarHistory,
      customTasks,
      customSchedule,
      settings,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cleaning-schedule-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setToastMsg('Data exported');
  }, [weekKey, tasks, calendarHistory, customTasks, customSchedule, settings]);

  // --- Clear all ---
  const clearAll = useCallback(() => {
    setTasks({});
    setCalendarHistory({});
    setCustomTasks([]);
    setCustomSchedule(null);
    setSettings(DEFAULT_SETTINGS);
    persistence.reset(weekKey);
    persistence.removeLocal(KEYS.calendarHistory);
    persistence.removeLocal(KEYS.customTasks);
    persistence.removeLocal(KEYS.customSchedule);
    persistence.removeLocal(KEYS.settings);
    setPage('schedule');
    setToastMsg('All data cleared');
    syncToCloud();
  }, [weekKey, syncToCloud]);

  const pendingCustomTasks = customTasks.filter((t) => !t.completed).length;

  // --- Auth loading ---
  if (authLoading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
        <p className="app-loading-text">Loading...</p>
      </div>
    );
  }

  // --- Auth gate ---
  if (!authUser) {
    return (
      <div className="app">
        <AuthPage onAuth={handleAuth} />
      </div>
    );
  }

  // --- Data loading ---
  if (!loaded) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
        <p className="app-loading-text">Loading your schedule...</p>
      </div>
    );
  }

  return (
    <div className="app">
      {page === 'schedule' && (
        <ScheduleView
          tasks={tasks}
          onToggleTask={toggleTask}
          onReset={resetWeek}
          schedule={effectiveSchedule}
          activeDays={settings.activeDays}
          householdName={settings.householdName}
          username={authUser.username}
        />
      )}
      {page === 'calendar' && (
        <MonthlyCalendar history={calendarHistory} schedule={effectiveSchedule} />
      )}
      {page === 'tasks' && (
        <CustomTasksPage
          tasks={customTasks}
          onAdd={addCustomTask}
          onToggle={toggleCustomTask}
          onDelete={deleteCustomTask}
          showToast={setToastMsg}
        />
      )}
      {page === 'settings' && (
        <SettingsPage
          settings={settings}
          onUpdate={updateSettings}
          onNavigate={setPage}
          onExport={exportData}
          onClearAll={clearAll}
          showToast={setToastMsg}
          showConfirm={setConfirmConfig}
          onLogout={handleLogout}
          username={authUser.username}
        />
      )}
      {page === 'customize' && (
        <CustomizeTaskListPage
          customSchedule={customSchedule}
          onSave={saveCustomSchedule}
          onNavigateBack={() => setPage('settings')}
          showToast={setToastMsg}
        />
      )}

      {page !== 'customize' && (
        <BottomNav
          activePage={page}
          onNavigate={setPage}
          pendingTaskCount={pendingCustomTasks}
        />
      )}

      <Toast message={toastMsg} onDismiss={() => setToastMsg(null)} />
      <ConfirmDialog config={confirmConfig} onClose={() => setConfirmConfig(null)} />
    </div>
  );
}

export default App;
